/**
 * ``AnnotationDeleteConfirmDialog`` — the confirmation dialog for deleting an
 * annotation (ADR-034 batch 6, #6). Mounted once (in the palette); shown
 * whenever a delete affordance calls
 * :func:`useAnnotationDeleteConfirmStore.requestDelete`. The actual
 * :func:`useDeleteAnnotation` only fires on **Delete** (confirm); **Cancel** /
 * dismiss closes without deleting. A Radix ``Dialog`` (role ``alertdialog``);
 * Cancel is the default focus so a stray Enter doesn't delete.
 *
 * Cross-references:
 *   - ADR-034 batch 6 (#6); feature spec §8.2.
 *   - @/annotations/{annotation-delete-confirm-store, use-annotations, annotation-tool-store}.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useDeleteAnnotation } from "@/annotations/use-annotations";
import { useAnnotationToolStore } from "@/annotations/annotation-tool-store";
import { useAnnotationDeleteConfirmStore } from "@/annotations/annotation-delete-confirm-store";

export function AnnotationDeleteConfirmDialog(): React.ReactElement {
  const pendingDeleteId = useAnnotationDeleteConfirmStore((state) => state.pendingDeleteId);
  const clearPendingDelete = useAnnotationDeleteConfirmStore((state) => state.clearPendingDelete);
  const deleteAnnotation = useDeleteAnnotation();
  const setSelectedId = useAnnotationToolStore((state) => state.setSelectedId);

  const confirm = (): void => {
    if (pendingDeleteId !== null) {
      deleteAnnotation.mutate(pendingDeleteId);
      setSelectedId(null);
    }
    clearPendingDelete();
  };

  return (
    <Dialog.Root
      open={pendingDeleteId !== null}
      onOpenChange={(open) => {
        if (!open) {
          clearPendingDelete();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          role="alertdialog"
          data-testid="annotation-delete-dialog"
          className="fixed left-1/2 top-1/2 z-50 w-80 -translate-x-1/2 -translate-y-1/2 rounded border border-border bg-bg-surface p-4 shadow-lg"
        >
          <Dialog.Title className="text-body font-medium text-text-primary">
            Delete annotation?
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-body-sm text-text-secondary">
            This permanently removes the annotation from the map and can't be undone.
          </Dialog.Description>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              data-testid="annotation-delete-cancel"
              onClick={clearPendingDelete}
              className="rounded border border-border px-3 py-1 text-body-sm text-text-secondary hover:bg-bg-elevated"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="annotation-delete-confirm"
              onClick={confirm}
              className="rounded bg-destructive px-3 py-1 text-body-sm text-bg-base hover:opacity-90"
            >
              Delete
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
